import {ArchiveConfirmationDialog} from '@github-ui/agent-sessions/components/ArchiveConfirmationDialog'
import {ArchiveTaskBanner} from '@github-ui/agent-sessions/components/ArchiveTaskBanner'
import {useChatPayload} from '@github-ui/agent-sessions/components/task-view/hooks/use-chat-payload'
import {ArchiveDialogProvider} from '@github-ui/agent-sessions/contexts/ArchiveDialogContext'
import {ChatStreamProvider} from '@github-ui/agent-sessions/contexts/ChatStreamContext'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {PullContextProvider} from '@github-ui/agent-sessions/contexts/PullContext'
import {ViewModeProvider} from '@github-ui/agent-sessions/contexts/ViewModeContext'
import {useInitializeAuthToken} from '@github-ui/agent-sessions/hooks/use-initialize-auth-token'
import {useRepoInfo} from '@github-ui/agent-sessions/hooks/use-repo-info'
import {useTask} from '@github-ui/agent-sessions/hooks/use-task'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {useTaskPermissions} from '@github-ui/issue-agent-tasks/hooks/use-task-permissions'
import {RelayEnvironmentProvider, relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {Spinner} from '@primer/react'
import {Component, lazy, type ReactNode, Suspense, useMemo} from 'react'

import styles from './agent-task-content.module.css'

const TaskChat = lazy(async () => {
  const m = await import('@github-ui/agent-sessions/components/task-view/TaskChat')
  return {default: m.TaskChat}
})

const relayEnvironment = relayEnvironmentWithMissingFieldHandlerForNode()

class TaskChatErrorBoundary extends Component<{children: ReactNode}, {error: Error | null}> {
  override state: {error: Error | null} = {error: null}

  static getDerivedStateFromError(error: Error) {
    return {error}
  }

  override render() {
    if (this.state.error) {
      return (
        <div className={styles.emptyState} role="alert">
          Unable to load task chat: {this.state.error.message}
        </div>
      )
    }
    return this.props.children
  }
}

export function AgentTaskSidePanelContent({taskId, repoId}: {taskId: string; repoId: number}) {
  return (
    <div className={styles.container}>
      <ConsecutiveCAPI401sProvider>
        <AgentTaskSidePanelInner taskId={taskId} repoId={repoId} />
      </ConsecutiveCAPI401sProvider>
    </div>
  )
}

function AgentTaskSidePanelInner({taskId, repoId}: {taskId: string; repoId: number}) {
  const repoIds = useMemo(() => [repoId], [repoId])
  const {data: repoMap, isLoading: isLoadingRepoInfo, isError: isRepoInfoError} = useRepoInfo(repoIds)
  const repoInfo = repoMap[repoId]

  const owner = repoInfo?.ownerLogin ?? ''
  const repo = repoInfo?.name ?? ''

  const {data: taskData, isLoading: isLoadingTask, isError: isTaskError} = useTask({owner, repo, taskId})
  const {data: chatPayload, isLoading: isLoadingChat, isError: isChatError} = useChatPayload()
  const {data: taskPermissions, isLoading: isLoadingPermissions} = useTaskPermissions(owner, repo, taskId)
  const currentUserCanPush = taskPermissions?.currentUserCanPush ?? false
  const steeringEnabled = taskPermissions?.steeringEnabled ?? false
  const pull = taskPermissions?.pull
  const currentUser = taskPermissions?.currentUser
  const sessionOwners = taskPermissions?.sessionOwners ?? []
  const initialAuthToken = taskPermissions?.initialAuthToken

  useInitializeAuthToken(initialAuthToken)

  const sessions = taskData?.sessions ?? []

  const currentUserIsSessionOwner = !!(currentUser && sessions.some(s => s.user_id === currentUser.id))

  const repository = useMemo(
    () =>
      ({
        id: repoId,
        name: repo,
        ownerLogin: owner,
        currentUserCanPush,
      }) as Repository,
    [repoId, repo, owner, currentUserCanPush],
  )

  if (isLoadingTask || isLoadingRepoInfo || isLoadingChat || isLoadingPermissions) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" srText="Loading task chat" />
      </div>
    )
  }

  if (isTaskError || isRepoInfoError || !repoInfo) {
    return (
      <div className={styles.emptyState} role="alert">
        Unable to load task data.
      </div>
    )
  }

  if (!taskData || sessions.length === 0) {
    return (
      <div className={styles.emptyState} role="status">
        No session information available.
      </div>
    )
  }

  if (isChatError || !chatPayload) {
    return (
      <div className={styles.emptyState} role="alert">
        Unable to load Copilot Chat. Ensure Copilot is enabled.
      </div>
    )
  }

  return (
    <ViewModeProvider mode="sidePanel">
      <CurrentRepositoryProvider repository={repository}>
        <CopilotChatProvider
          workerPath={chatPayload.findFileWorkerPath}
          threadId={null}
          refs={chatPayload.refs ?? []}
          mode="assistive"
          ssoOrganizations={chatPayload.ssoOrganizations ?? []}
          copilotChatPayload={chatPayload}
        >
          <ChatStreamProvider>
            <ArchiveDialogProvider>
              <ArchiveTaskBanner />
              <CurrentUserProvider
                currentUser={currentUser}
                currentUserIsSessionOwner={currentUserIsSessionOwner}
                optedInToFeedback={chatPayload.optedInToUserFeedback}
              >
                <PullContextProvider pull={pull}>
                  <RelayEnvironmentProvider environment={relayEnvironment}>
                    <TaskChatErrorBoundary key={taskId}>
                      <Suspense fallback={<Spinner size="medium" srText="Loading task chat" />}>
                        <TaskChat
                          taskId={taskId}
                          sessions={sessions}
                          currentUser={currentUser}
                          currentUserCanPush={currentUserCanPush}
                          steeringEnabled={steeringEnabled}
                          sessionOwners={sessionOwners}
                          logsPollingInterval={3000}
                          repoOwner={owner}
                          repoName={repo}
                          task={taskData}
                        />
                      </Suspense>
                    </TaskChatErrorBoundary>
                  </RelayEnvironmentProvider>
                </PullContextProvider>
              </CurrentUserProvider>
              <ArchiveConfirmationDialog />
            </ArchiveDialogProvider>
          </ChatStreamProvider>
        </CopilotChatProvider>
      </CurrentRepositoryProvider>
    </ViewModeProvider>
  )
}
